import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaLeaf, FaUser } from "react-icons/fa";
import { FiUserPlus, FiClock } from "react-icons/fi";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { PasswordField } from "../../../ui/PasswordField";
import * as S from "../Login.styled";
import { useAuth } from "../../../context/AuthContext";
import { getHomeRouteForRole } from "../../../Utils/homeRoute";
import PasswordStrengthMeter from "../PasswordStrengthMeter";
import VillagerSignupWizard from "./VillagerSignupWizard";

import SocialAuthButtons, { useSocialSignup, VerifiedSocialAccount, socialErrorText } from "../SocialAuth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ORDER = ["email", "password", "confirmPassword", "firstName", "lastName", "businessName"];

/**
 * Reads ?role= so a farmer-facing CTA can land on the right form already
 * selected. Only "villager" opts in; anything else (absent, "client", garbage)
 * keeps the Client default, so a malformed link can never silently create the
 * wrong kind of account.
 */
const roleFromQuery = (searchParams) =>
  String(searchParams.get("role") || "").toLowerCase() === "villager"
    ? "Villager"
    : "Client";

/**
 * True when the link already states which kind of account this is, so the
 * farm/shopper question is skipped. Only the two exact values count: a garbage
 * or empty ?role= is not an answer, and asking beats guessing — the role is
 * fixed at registration and only an admin can change it afterwards.
 */
const roleAnsweredByQuery = (searchParams) =>
  ["villager", "client"].includes(
    String(searchParams.get("role") || "").toLowerCase()
  );

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const social = useSocialSignup(searchParams.get("social") === "1");
  const [formData, setFormData] = useState({
    // Carried over when the auth prompt sends someone here mid-task, so they don't retype it.
    email: searchParams.get("email") || "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    profilePictureUrl: "",
    userRole: roleFromQuery(searchParams),
    businessName: "",
    businessDescription: "",
    businessBannerPicUrl: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [roleAnswered, setRoleAnswered] = useState(() =>
    roleAnsweredByQuery(searchParams)
  );
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { register } = useAuth();
  const [notification, setNotification] = useState({ message: "", type: "success" });
  const [applicationPending, setApplicationPending] = useState(false);
  const isVillager = formData.userRole === "Villager";
  // The shopper form is asked in two halves: credentials, then who you are.
  // Nine fields at once read as a wall, and a name typed into a form that then
  // fails on the password is work thrown away. Social sign-up arrives with the
  // credentials already settled, so it opens on the second half.
  const [detailsStep, setDetailsStep] = useState(1);
  const onCredentials = detailsStep === 1 && !social.profile;

  useEffect(() => {
    if (social.profile) setFormData((previous) => ({ ...previous, email: social.profile.email || "",
      firstName: social.profile.firstName || previous.firstName, lastName: social.profile.lastName || previous.lastName,
      password: "", confirmPassword: "",
    }));
  }, [social.profile]);

  const validate = (values) => {
    const next = {};
    const required = t("login.field_required", { defaultValue: "This field is required" });
    if (!values.email.trim()) next.email = required;
    else if (!EMAIL_RE.test(values.email.trim())) {
      next.email = t("login.email_invalid", { defaultValue: "Enter a valid email address" });
    }
    if (!social.profile) {
    if (!values.password) next.password = required;
    else if (values.password.length < 10) {
      next.password = t("login.password_too_short", {
        defaultValue: "Password must be at least 10 characters.",
      });
    } else if (
      !/[A-Z]/.test(values.password) ||
      !/[a-z]/.test(values.password) ||
      !/[0-9]/.test(values.password) ||
      !/[^a-zA-Z0-9]/.test(values.password)
    ) {
      next.password = t("login.password_weak", {
        defaultValue: "Add an uppercase, lowercase, number, and special character.",
      });
    }
    if (!values.confirmPassword) next.confirmPassword = required;
    else if (values.confirmPassword !== values.password) {
      next.confirmPassword = t("login.passwords_mismatch", {
        defaultValue: "Passwords do not match.",
      });
    }
    }
    if (!values.firstName.trim()) next.firstName = required;
    if (!values.lastName.trim()) next.lastName = required;
    if (values.userRole === "Villager" && !values.businessName.trim()) {
      next.businessName = t("login.business_name_required", {
        defaultValue: "Business name is required.",
      });
    }
    return next;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const next = { ...formData, [name]: value };
    setFormData(next);
    if (Object.keys(touched).length) setErrors(validate(next));
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors(validate(formData));
  };

  const handleRoleChange = (role) => {
    const next = {
      ...formData,
      userRole: role,
      ...(role === "Client"
        ? { businessName: "", businessDescription: "", businessBannerPicUrl: "" }
        : {}),
    };
    setFormData(next);
    if (Object.keys(touched).length) setErrors(validate(next));
  };

  const answerRoleQuestion = (role) => {
    handleRoleChange(role);
    setRoleAnswered(true);
  };

  /**
   * Validates one wizard step on demand and returns the id of its first invalid
   * field, or null. Computed fresh rather than read off `errors`: a step nobody
   * has typed in yet has no errors recorded, so trusting that state would wave
   * an entirely blank step through.
   */
  const validateStep = (fields) => {
    const validation = validate(formData);
    setErrors(validation);
    setTouched((prev) => ({
      ...prev,
      ...fields.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    }));
    return fields.find((field) => validation[field]) || null;
  };

  const setUploadedField = (field, url) =>
    setFormData((prev) => ({ ...prev, [field]: url }));

  const showNotification = (message, type) => {
    setNotification({ message, type });
    window.setTimeout(() => {
      setNotification({ message: "", type: "success" });
    }, 3000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validation = validate(formData);
    setErrors(validation);
    setTouched(FIELD_ORDER.reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    if (Object.keys(validation).length > 0) {
      const first = FIELD_ORDER.find((key) => validation[key]);
      if (first) document.getElementById(first)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const result = social.profile ? await register(formData, social.profile.registrationKey) : await register(formData);

      // Farmer awaiting review: there is no session to use, so stay on the page
      // and say so. Redirecting would land on /login (or bounce off a protected
      // route) with no explanation of why they cannot get in.
      if (result?.requiresApproval) {
        setSubmitting(false);
        setApplicationPending(true);
        return;
      }

      if (result?.requiresEmailConfirmation) {
        showNotification(
          result?.message ||
            t("login.register_success_confirm", {
              defaultValue:
                "Registration successful. Please confirm your email before logging in.",
            }),
          "success"
        );
        window.setTimeout(() => {
          navigate(`/confirm-email?email=${encodeURIComponent(result.email || formData.email)}`);
        }, 1000);
        return;
      }
      showNotification(
        t("login.register_success", { defaultValue: "Registration successful!" }),
        "success"
      );
      // Someone sent here from the auth prompt was part-way through something — saving a farm, say
      // — so return them to it rather than to a generic landing page. Relative paths only, so the
      // query string can't be used to bounce anyone off-site.
      const returnTo = searchParams.get("returnTo");
      const safeReturn =
        returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : null;

      // Otherwise new shoppers land in the basket finder for a personalised pick;
      // villagers/others go to their role's home.
      const destination =
        safeReturn ||
        (result?.role === "Client"
          ? "/find-my-basket?welcome=1"
          : getHomeRouteForRole(result?.role));
      window.setTimeout(() => navigate(destination), 1000);
    } catch (error) {
      setSubmitting(false);
      const message =
        (social.profile ? socialErrorText(t, error?.response?.data?.code) : null) ||
        error?.response?.data?.message ||
        error?.response?.data ||
        t("login.register_error", { defaultValue: "Registration failed. Please check your details." });
      showNotification(message, "error");
    }
  };

  if (social.loading || social.error) {
    return <S.Page><S.FormWrapper aria-busy={social.loading}>
      {social.loading ? <p role="status">{t("social_auth.loading", { defaultValue: "Checking your sign-in..." })}</p> : <>
        <S.Notification $type="error" role="alert">{socialErrorText(t, social.error)}</S.Notification>
        <Button onClick={() => navigate("/signup", { replace: true })}>{t("social_auth.restart", { defaultValue: "Start again" })}</Button>
      </>}
    </S.FormWrapper></S.Page>;
  }

  // Terminal state for a farmer whose account exists but is awaiting review.
  // There is no session yet, so there is nowhere useful to navigate to.
  if (applicationPending) {
    return (
      <S.Page>
        <S.FormWrapper data-testid="application-pending">
          <S.FormHeader>
            <S.HeaderIcon aria-hidden="true">
              <FiClock />
            </S.HeaderIcon>
            <h1>{t("villager.pending.title", { defaultValue: "Application received" })}</h1>
            <S.Subtitle>
              {t("villager.pending.body", {
                defaultValue:
                  "Thanks for applying. We review each farm by hand, usually within a day. We'll email you at {{email}} as soon as your farm is approved, and then you can sign in and add your products.",
                email: formData.email,
              })}
            </S.Subtitle>
          </S.FormHeader>
          <Button type="button" variant="primary" fullWidth onClick={() => navigate("/")}>
            {t("villager.pending.back_home", { defaultValue: "Back to the marketplace" })}
          </Button>
        </S.FormWrapper>
      </S.Page>
    );
  }

  // Step one. The account's role is fixed at registration — only an admin can
  // change it later — and "Villager" meant nothing to a farmer looking for a
  // way in, so the choice is asked as a plain question before any typing.
  if (!roleAnswered) {
    return (
      <S.Page>
        <S.FormWrapper data-testid="role-question">
          <S.FormHeader>
            <S.HeaderIcon aria-hidden="true">
              <FiUserPlus />
            </S.HeaderIcon>
            <h1>{t("login.role_question", { defaultValue: "Are you a farm?" })}</h1>
            <S.Subtitle>
              {t("login.role_question_body", {
                defaultValue:
                  "This sets up the right kind of account. Pick the one that fits you.",
              })}
            </S.Subtitle>
          </S.FormHeader>
          <S.ChoiceList>
            <S.ChoiceCard
              type="button"
              data-testid="choose-villager"
              onClick={() => answerRoleQuestion("Villager")}
            >
              <S.ChoiceIcon aria-hidden="true">
                <FaLeaf />
              </S.ChoiceIcon>
              <S.ChoiceBody>
                <S.ChoiceTitle>
                  {t("login.role_question_farm", {
                    defaultValue: "Yes, I have a farm",
                  })}
                </S.ChoiceTitle>
                <S.ChoiceHint>
                  {t("login.role_question_farm_hint", {
                    defaultValue:
                      "Sell your produce and baskets. We review each farm by hand before it goes live.",
                  })}
                </S.ChoiceHint>
              </S.ChoiceBody>
            </S.ChoiceCard>
            <S.ChoiceCard
              type="button"
              data-testid="choose-client"
              onClick={() => answerRoleQuestion("Client")}
            >
              <S.ChoiceIcon aria-hidden="true">
                <FaUser />
              </S.ChoiceIcon>
              <S.ChoiceBody>
                <S.ChoiceTitle>
                  {t("login.role_question_shopper", {
                    defaultValue: "No, I want to shop",
                  })}
                </S.ChoiceTitle>
                <S.ChoiceHint>
                  {t("login.role_question_shopper_hint", {
                    defaultValue:
                      "Browse local farms and order fresh baskets to your door.",
                  })}
                </S.ChoiceHint>
              </S.ChoiceBody>
            </S.ChoiceCard>
          </S.ChoiceList>
          <S.ToggleText>
            {t("login.toggle_existing", { defaultValue: "Already have an account?" })}{" "}
            <S.ToggleAction type="button" onClick={() => navigate("/login")}>
              {t("login.toggle_login", { defaultValue: "Log In" })}
            </S.ToggleAction>
          </S.ToggleText>
        </S.FormWrapper>
      </S.Page>
    );
  }

  // A farm has three separate things to set up — login, person, farm — so it
  // gets a step per thing instead of one nine-field screen. Shoppers have five
  // fields and keep the short form below.
  if (isVillager) {
    return (
      <S.Page>
        <VillagerSignupWizard
          socialProfile={social.profile}
          formData={formData}
          errors={errors}
          touched={touched}
          submitting={submitting}
          notification={notification}
          onChange={handleChange}
          onBlur={handleBlur}
          onFieldUpload={setUploadedField}
          onRoleChange={handleRoleChange}
          onValidateStep={validateStep}
          onSubmit={handleSubmit}
          onLoginClick={() => navigate("/login")}
        />
      </S.Page>
    );
  }

  return (
    <S.Page>
      <S.FormWrapper>
        <S.FormHeader>
          <S.HeaderIcon aria-hidden="true">
            <FiUserPlus />
          </S.HeaderIcon>
          <h1>{t("login.create_account", { defaultValue: "Create your account" })}</h1>
          <S.Subtitle>
            {t("login.subtitle_signup", {
              defaultValue: "Join Dora e Fshatit and bring the village to your door.",
            })}
          </S.Subtitle>
        </S.FormHeader>
        {social.profile ? <VerifiedSocialAccount profile={social.profile} /> : <SocialAuthButtons role="Client" />}
        <S.Form onSubmit={handleSubmit} noValidate>
          {onCredentials && <>
          <S.FieldGroup>
            <S.FieldLabel>{t("login.account_type", { defaultValue: "Account type" })}</S.FieldLabel>
            <S.RoleToggle>
              <S.RoleOption
                type="button"
                $active={!isVillager}
                onClick={() => handleRoleChange("Client")}
                aria-pressed={!isVillager}
              >
                <S.RoleIcon aria-hidden="true">
                  <FaUser />
                </S.RoleIcon>
                {t("login.account_client", { defaultValue: "Client" })}
              </S.RoleOption>
              <S.RoleOption
                type="button"
                $active={isVillager}
                onClick={() => handleRoleChange("Villager")}
                aria-pressed={isVillager}
              >
                <S.RoleIcon aria-hidden="true">
                  <FaLeaf />
                </S.RoleIcon>
                {t("login.account_villager", { defaultValue: "Villager" })}
              </S.RoleOption>
            </S.RoleToggle>
          </S.FieldGroup>
          </>}
          {(onCredentials || social.profile) && <Input
            id="email"
            name="email"
            readOnly={Boolean(social.profile)}
            label={t("login.email", { defaultValue: "Email" })}
            placeholder={t("login.email_placeholder", { defaultValue: "example@example.com" })}
            type="email"
            inputMode="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={touched.email ? errors.email : undefined}
            required
          />}
          {onCredentials && <>
          <S.FieldGroup>
            <PasswordField
              id="password"
              name="password"
              label={t("login.password", { defaultValue: "Password" })}
              placeholder={t("login.password_placeholder", { defaultValue: "Password" })}
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              errorText={touched.password ? errors.password : undefined}
              required
            />
            <PasswordStrengthMeter password={formData.password} />
          </S.FieldGroup>
          <PasswordField
            id="confirmPassword"
            name="confirmPassword"
            label={t("login.confirm_password", { defaultValue: "Confirm Password" })}
            placeholder={t("login.confirm_password_placeholder", { defaultValue: "Confirm Password" })}
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={touched.confirmPassword ? errors.confirmPassword : undefined}
            required
          />
          </>}
          {!onCredentials && <>
          <S.FieldRow>
            <Input
              id="firstName"
              name="firstName"
              label={t("login.first_name", { defaultValue: "First Name" })}
              placeholder={t("login.first_name_placeholder", { defaultValue: "First Name" })}
              type="text"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              errorText={touched.firstName ? errors.firstName : undefined}
              required
            />
            <Input
              id="lastName"
              name="lastName"
              label={t("login.last_name", { defaultValue: "Last Name" })}
              placeholder={t("login.last_name_placeholder", { defaultValue: "Last Name" })}
              type="text"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              errorText={touched.lastName ? errors.lastName : undefined}
              required
            />
          </S.FieldRow>
          {/* Uploading needs a signed-in token and nobody is signed in yet, so the
              photo is set from the profile once the account exists. */}
          <S.Subtitle>
            {t("login.photo_after_signup", {
              defaultValue: "You can add a profile photo from your profile once you're in.",
            })}
          </S.Subtitle>
          </>}
          {notification.message ? (
            <S.Notification $type={notification.type} role="status">
              {notification.message}
            </S.Notification>
          ) : null}
          {onCredentials ? (
            <Button
              type="button"
              variant="primary"
              fullWidth
              onClick={() => {
                const invalid = validateStep(["email", "password", "confirmPassword"]);
                if (!invalid) setDetailsStep(2);
              }}
            >
              {t("login.continue", { defaultValue: "Continue" })}
            </Button>
          ) : (
            <>
              <Button type="submit" variant="primary" fullWidth loading={submitting}>
                {t("login.submit", { defaultValue: "Register" })}
              </Button>
              {!social.profile && (
                <Button type="button" variant="ghost" fullWidth onClick={() => setDetailsStep(1)}>
                  {t("login.back", { defaultValue: "Back" })}
                </Button>
              )}
            </>
          )}
        </S.Form>
        <S.ToggleText>
          {t("login.toggle_existing", { defaultValue: "Already have an account?" })}{" "}
          <S.ToggleAction type="button" onClick={() => navigate("/login")}>
            {t("login.toggle_login", { defaultValue: "Log In" })}
          </S.ToggleAction>
        </S.ToggleText>
      </S.FormWrapper>
    </S.Page>
  );
};

export default SignupPage;
